import React, { useState } from "react";
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import { useTheme, alpha } from '@mui/material/styles';

// Utility to safely get nested values like "customer.firstName"
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((acc, key) => {
    return acc && acc[key] !== undefined ? acc[key] : null;
  }, obj);
};

/**
 * Reusable Table Component
 * 
 * @param {Array} columns - Array of column definitions with key, label, sortable, render, etc.
 * @param {Array} data - Array of data objects to display
 * @param {Function} onRowClick - Optional callback when row is clicked
 * @param {String} emptyMessage - Message to display when no data
 * @param {Boolean} striped - Enable striped rows
 * @param {Boolean} hoverable - Enable hover effect
 * @param {Boolean} dense - Use compact padding
 * @param {Function} onEdit - Optional callback when edit starts
 * @param {Function} onSave - Optional callback when save is clicked
 * @param {Function} onCancel - Optional callback when cancel is clicked
 * @param {String} rowKey - Key to use for row identification (default: 'id')
 */
export function Table({ 
  columns, 
  data, 
  onRowClick, 
  emptyMessage = "No data available",
  striped = true,
  hoverable = true,
  dense = false,
  onEdit = null,
  onSave = null,
  onCancel = null,
  rowKey = 'id',
  searchable = false,
  searchPlaceholder = "Search...",
  paginated = false,
  pageSize = 10,
  expandable = false,
  renderExpandedRow = null,
  /** MUI data-grid styling (search field, Table + Paper, Cards on mobile, outlined pagination). */
  material = false
}) {
  const theme = useTheme();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [editingRowId, setEditingRowId] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const handleSort = (key) => {
    if (!columns.find(col => col.key === key)?.sortable) return;
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filter data based on search (search all visible columns by default)
  const filteredData = data.filter(row => {
    if (!searchTerm) return true;
    // Only search columns that are visible (not hidden)
    const searchCols = columns.filter(col => col && col.key && col.label !== false);
    return searchCols.some(col => {
      const value = getNestedValue(row, col.key);
      return value && value.toString().toLowerCase().includes(searchTerm.toLowerCase());
    });
  });

  // Sort filtered data
  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aValue = getNestedValue(a, sortConfig.key);
    const bValue = getNestedValue(b, sortConfig.key);
    if (aValue == null) return 1;
    if (bValue == null) return -1;
    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination
  const totalPages = paginated ? Math.ceil(sortedData.length / pageSize) : 1;
  const startIndex = paginated ? (currentPage - 1) * pageSize : 0;
  const endIndex = paginated ? startIndex + pageSize : sortedData.length;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleRowClick = (row) => {
    if (editingRowId === row[rowKey]) return;
    if (expandable) {
      setExpandedRowId(prev => prev === row[rowKey] ? null : row[rowKey]);
    }
    if (onRowClick) onRowClick(row);
  };

  const handleEdit = (row) => {
    setEditingRowId(row[rowKey]);
    setEditedData({ ...row });
    setSaveError('');
    if (onEdit) onEdit(row);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError('');
    try {
      const saveResult = onSave ? await onSave(editedData) : true;
      if (saveResult === false) {
        setSaveError('Could not save changes. Please review and try again.');
        return;
      }
      setEditingRowId(null);
      setEditedData({});
    } catch (error) {
      setSaveError(error?.message || 'Could not save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (onCancel) onCancel();
    setEditingRowId(null);
    setEditedData({});
  };

  const handleFieldChange = (key, value) => {
    setEditedData(prev => ({ ...prev, [key]: value }));
  };

  const renderCell = (row, column) => {
    const isEditing = editingRowId === row[rowKey];

    // Helper to safely render any value
    const safeRender = (val) => {
      if (React.isValidElement(val)) return val;
      if (typeof val === 'object' && val !== null) return JSON.stringify(val);
      if (val === undefined || val === null) return '-';
      return String(val);
    };

    if (column.render && !column.editable) {
      const rendered = column.render(getNestedValue(row, column.key), row, {
        isEditing,
        handleEdit: () => handleEdit(row),
        handleSave,
        handleCancel: handleCancelEdit,
        isSaving,
        saveError,
      });
      return safeRender(rendered);
    }

    if (isEditing && column.editable) {
      const value = editedData[column.key] ?? '';
      if (column.editType === 'select' && column.options) {
        if (material) {
          return (
            <FormControl fullWidth size="small" onClick={(e) => e.stopPropagation()}>
              <Select
                value={value}
                onChange={(e) => handleFieldChange(column.key, e.target.value)}
                displayEmpty
              >
                {column.options.map((opt) => (
                  <MenuItem key={String(opt.value)} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }
        return (
          <select
            value={value}
            onChange={(e) => handleFieldChange(column.key, e.target.value)}
            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            onClick={(e) => e.stopPropagation()}
          >
            {column.options.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );
      }
      if (column.editType === 'textarea') {
        if (material) {
          return (
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={value}
              onChange={(e) => handleFieldChange(column.key, e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          );
        }
        return (
          <textarea
            value={value}
            onChange={(e) => handleFieldChange(column.key, e.target.value)}
            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            onClick={(e) => e.stopPropagation()}
            rows={2}
          />
        );
      }
      if (material) {
        return (
          <TextField
            type={column.editType || 'text'}
            fullWidth
            size="small"
            value={value}
            onChange={(e) => handleFieldChange(column.key, e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        );
      }
      return (
        <input
          type={column.editType || 'text'}
          value={value}
          onChange={(e) => handleFieldChange(column.key, e.target.value)}
          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          onClick={(e) => e.stopPropagation()}
        />
      );
    }

    if (column.render) {
      const rendered = column.render(getNestedValue(row, column.key), row, {
        isEditing: false,
        handleEdit: () => handleEdit(row),
        handleSave,
        handleCancel: handleCancelEdit,
        isSaving,
        saveError,
      });
      return safeRender(rendered);
    }

    return safeRender(getNestedValue(row, column.key));
  };

  const paddingClass = dense ? "px-4 py-2" : "px-6 py-4";

  if (material) {
    return (
      <Box>
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError('')}>
            {saveError}
          </Alert>
        )}
        {searchable && (
          <Box sx={{ mb: 2, maxWidth: { md: 480 } }}>
            <TextField
              fullWidth
              size="small"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={searchPlaceholder}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
            {searchTerm && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Found {sortedData.length} result{sortedData.length !== 1 ? 's' : ''}
              </Typography>
            )}
          </Box>
        )}

        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ display: { xs: 'none', md: 'block' }, borderRadius: 1.5, overflow: 'auto' }}
        >
          <MuiTable size={dense ? 'small' : 'medium'} stickyHeader>
            <TableHead>
              <TableRow>
                {expandable && <TableCell padding="checkbox" sx={{ bgcolor: 'grey.100' }} />}
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    className={column.className}
                    sx={{ fontWeight: 600, bgcolor: 'grey.100' }}
                  >
                    {column.sortable ? (
                      <TableSortLabel
                        active={sortConfig.key === column.key}
                        direction={sortConfig.key === column.key ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort(column.key)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + (expandable ? 1 : 0)}
                    align="center"
                    sx={{ py: 4, color: 'text.secondary' }}
                  >
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row, rowIndex) => (
                  <React.Fragment key={row[rowKey] || rowIndex}>
                    <TableRow
                      hover={hoverable && editingRowId !== row[rowKey]}
                      onClick={() => handleRowClick(row)}
                      sx={{
                        cursor:
                          (onRowClick || expandable) && editingRowId !== row[rowKey] ? 'pointer' : 'default',
                        backgroundColor: editingRowId === row[rowKey]
                          ? alpha(theme.palette.warning.main, 0.12)
                          : expandedRowId === row[rowKey]
                            ? alpha(theme.palette.primary.main, 0.06)
                            : striped && rowIndex % 2 === 1
                              ? 'grey.50'
                              : 'background.paper',
                      }}
                    >
                      {expandable && (
                        <TableCell
                          padding="checkbox"
                          onClick={(e) => e.stopPropagation()}
                          sx={{ verticalAlign: 'middle' }}
                        >
                          <span
                            style={{
                              transform: expandedRowId === row[rowKey] ? 'rotate(90deg)' : 'rotate(0deg)',
                              display: 'inline-block',
                              transition: 'transform 0.2s',
                            }}
                          >
                            ▶
                          </span>
                        </TableCell>
                      )}
                      {columns.map((column) => (
                        <TableCell key={column.key} className={column.cellClassName} sx={{ verticalAlign: 'top' }}>
                          {renderCell(row, column)}
                        </TableCell>
                      ))}
                    </TableRow>
                    {expandable && expandedRowId === row[rowKey] && renderExpandedRow && (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length + 1}
                          sx={{ bgcolor: 'grey.50', borderTop: 1, borderColor: 'divider' }}
                        >
                          <Box sx={{ p: 2 }}>{renderExpandedRow(row)}</Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </MuiTable>
        </TableContainer>

        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          {paginatedData.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', color: 'text.secondary', borderRadius: 1.5 }}>
              {emptyMessage}
            </Paper>
          ) : (
            paginatedData.map((row, rowIndex) => (
              <Card
                key={row[rowKey] || rowIndex}
                variant="outlined"
                onClick={() => handleRowClick(row)}
                sx={{
                  mb: 1.5,
                  borderRadius: 1.5,
                  backgroundColor: editingRowId === row[rowKey] ? alpha(theme.palette.warning.main, 0.1) : 'background.paper',
                  ...((onRowClick || expandable) && editingRowId !== row[rowKey]
                    ? { cursor: 'pointer' }
                    : {}),
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  {expandable && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {expandedRowId === row[rowKey] ? 'Click to collapse' : 'Click to expand'}
                      </Typography>
                      <span
                        style={{
                          transform: expandedRowId === row[rowKey] ? 'rotate(90deg)' : 'rotate(0deg)',
                          display: 'inline-block',
                          transition: 'transform 0.2s',
                        }}
                      >
                        ▶
                      </span>
                    </Stack>
                  )}
                  {columns.map((column) => (
                    <Box
                      key={column.key}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 2,
                        py: 1,
                        borderBottom: 1,
                        borderColor: 'divider',
                        '&:last-of-type': { borderBottom: 0 },
                      }}
                    >
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                        sx={{ flexShrink: 0 }}
                      >
                        {column.label}
                      </Typography>
                      <Typography
                        component="div"
                        variant="body2"
                        textAlign="right"
                        sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}
                      >
                        {renderCell(row, column)}
                      </Typography>
                    </Box>
                  ))}
                  {expandable && expandedRowId === row[rowKey] && renderExpandedRow && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>{renderExpandedRow(row)}</Box>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </Box>

        {paginated && totalPages > 1 && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            sx={{ mt: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {startIndex + 1} to {Math.min(endIndex, sortedData.length)} of {sortedData.length} results
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  const showPage = page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1);
                  const showEllipsis =
                    (page === 2 && currentPage > 3) || (page === totalPages - 1 && currentPage < totalPages - 2);
                  if (showEllipsis) {
                    return (
                      <span key={page} style={{ padding: '0 8px' }}>
                        …
                      </span>
                    );
                  }
                  if (!showPage) return null;
                  return (
                    <Button
                      key={page}
                      size="small"
                      variant={currentPage === page ? 'contained' : 'outlined'}
                      onClick={() => setCurrentPage(page)}
                      sx={{ minWidth: 40 }}
                    >
                      {page}
                    </Button>
                  );
                })}
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </Box>
          </Stack>
        )}
      </Box>
    );
  }

  return (
    <>
      {saveError && (
        <div className="mb-3 p-3 rounded-md border border-red-200 bg-red-50 text-red-700 text-sm">
          {saveError}
        </div>
      )}
      {searchable && (
        <div className="mb-4">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full md:w-96 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {searchTerm && (
            <p className="text-sm text-gray-600 mt-2">
              Found {sortedData.length} result{sortedData.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      )}

      {/* Desktop Table */}
      <div className="hidden md:block w-full overflow-x-auto rounded-md border border-gray-200 shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {expandable && <th className={`${paddingClass} w-8`}></th>}
              {columns.map((column) => (
                <th
                  key={column.key}
                  onClick={() => column.sortable && handleSort(column.key)}
                  className={`${paddingClass} text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${column.sortable ? 'cursor-pointer hover:bg-gray-100 select-none' : ''} ${column.className || ''}`}
                >
                  <div className="flex items-center gap-2">
                    {column.label}
                    {column.sortable && sortConfig.key === column.key && (
                      <span className="text-gray-400">
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className={`${paddingClass} text-center text-gray-500`}>
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              paginatedData.map((row, rowIndex) => (
                <React.Fragment key={row[rowKey] || rowIndex}>
                  <tr
                    onClick={() => handleRowClick(row)}
                    className={`${striped && rowIndex % 2 === 1 ? 'bg-gray-50' : ''} ${hoverable && editingRowId !== row[rowKey] ? 'hover:bg-blue-50 transition-colors' : ''} ${editingRowId === row[rowKey] ? 'bg-yellow-50' : ''} ${expandedRowId === row[rowKey] ? 'bg-blue-50' : ''} ${(onRowClick || expandable) && editingRowId !== row[rowKey] ? 'cursor-pointer' : ''}`}
                  >
                    {expandable && (
                      <td className={`${paddingClass} text-sm text-gray-500 w-8`}>
                        <span className="transition-transform inline-block" style={{
                          transform: expandedRowId === row[rowKey] ? 'rotate(90deg)' : 'rotate(0deg)'
                        }}>
                          ▶
                        </span>
                      </td>
                    )}
                    {columns.map((column) => (
                      <td key={column.key} className={`${paddingClass} text-sm text-gray-900 ${column.cellClassName || ''}`}>
                        {renderCell(row, column)}
                      </td>
                    ))}
                  </tr>

                  {expandable && expandedRowId === row[rowKey] && renderExpandedRow && (
                    <tr>
                      <td colSpan={columns.length + 1} className="bg-gray-50 border-t border-gray-200">
                        <div className="p-4">{renderExpandedRow(row)}</div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card Layout */}
      <div className="md:hidden space-y-4">
        {paginatedData.length === 0 ? (
          <div className="text-center py-8 text-gray-500 bg-white rounded-md border border-gray-200">
            {emptyMessage}
          </div>
        ) : (
          paginatedData.map((row, rowIndex) => (
            <React.Fragment key={row[rowKey] || rowIndex}>
              <div
                onClick={() => handleRowClick(row)}
                className={`bg-white rounded-md border border-gray-200 p-4 shadow-sm ${editingRowId === row[rowKey] ? 'bg-yellow-50 border-yellow-300' : ''} ${expandedRowId === row[rowKey] ? 'bg-blue-50 border-blue-300' : ''} ${(onRowClick || expandable) && editingRowId !== row[rowKey] ? 'cursor-pointer active:bg-blue-50' : ''}`}
              >
                {expandable && (
                  <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
                    <span className="text-xs text-gray-500 font-medium">
                      {expandedRowId === row[rowKey] ? 'Click to collapse' : 'Click to expand'}
                    </span>
                    <span className="text-gray-400 transition-transform inline-block" style={{
                      transform: expandedRowId === row[rowKey] ? 'rotate(90deg)' : 'rotate(0deg)'
                    }}>
                      ▶
                    </span>
                  </div>
                )}
                
                {columns.map((column) => (
                  <div key={column.key} className="flex justify-between py-2 border-b border-gray-100 last:border-b-0">
                    <span className="text-sm font-medium text-gray-500">{column.label}:</span>
                    <span className="text-sm text-gray-900 text-right ml-4 flex-1">{renderCell(row, column)}</span>
                  </div>
                ))}
                
                {expandable && expandedRowId === row[rowKey] && renderExpandedRow && (
                  <div className="mt-4 pt-4 border-t border-gray-200">{renderExpandedRow(row)}</div>
                )}
              </div>
            </React.Fragment>
          ))
        )}
      </div>

      {/* Pagination */}
      {paginated && totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between m-4 gap-4">
          <div className="text-sm text-gray-600">
            Showing {startIndex + 1} to {Math.min(endIndex, sortedData.length)} of {sortedData.length} results
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            <div className="flex gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                const showPage = page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1);
                const showEllipsis = (page === 2 && currentPage > 3) || (page === totalPages - 1 && currentPage < totalPages - 2);
                if (showEllipsis) return <span key={page} className="px-2">...</span>;
                if (!showPage) return null;
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 border rounded ${currentPage === page ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 hover:bg-gray-50'}`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// PropTypes
Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      sortable: PropTypes.bool,
      editable: PropTypes.bool,
      editType: PropTypes.oneOf(['text', 'number', 'email', 'select', 'textarea']),
      options: PropTypes.arrayOf(
        PropTypes.shape({ value: PropTypes.any, label: PropTypes.string })
      ),
      render: PropTypes.func,
      className: PropTypes.string,
      cellClassName: PropTypes.string,
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  onRowClick: PropTypes.func,
  emptyMessage: PropTypes.string,
  striped: PropTypes.bool,
  hoverable: PropTypes.bool,
  dense: PropTypes.bool,
  onEdit: PropTypes.func,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  rowKey: PropTypes.string,
  searchable: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  paginated: PropTypes.bool,
  pageSize: PropTypes.number,
  expandable: PropTypes.bool,
  renderExpandedRow: PropTypes.func,
  material: PropTypes.bool,
};

export default Table;